import { z } from 'zod'
import { chatUsageSchema, threadEntrySchema } from './user-facing-entry.contract'

export const assistantProviderSchema = z.enum(['openai', 'anthropic'])

// Debug-only superadmin impersonation: operate on this actor's threads instead
// of the caller's own. Refused (FORBIDDEN) unless debug tools are on AND the
// caller is a super admin — see resolve-actor.ts. Omitted = the caller.
export const actorOverrideSchema = z.string().min(1).optional()

// A thread is addressed by its THREAD PATH: the list of threadIds from the root
// conversation down through every nested sub-agent. The first segment is always
// the top-level conversation id; deeper segments (debug-mode only) name nested
// sub-agent threads. Always non-empty.
export const threadPathSchema = z.array(z.string().min(1)).min(1)

// Wire shape of the in-flight envelope. Present iff the agentic loop is running;
// absent on idle / completed conversations. The server-internal `streamId` /
// `startedAt` lease never reaches the client, and the live "thinking…" text now
// rides in the turn's `processing` — so only the activity label and composing
// phase survive on the wire.
export const streamingSchema = z
  .object({
    currentActivity: z.string().optional(),
    currentPhase: z.enum(['composing']).optional(),
  })
  .strict()

// One grouped shape for both debug and non-debug. `entries` are always
// `threadEntrySchema`; the debug-only fields (doc metadata, plus — inside
// each entry — `assistant.response` and the full `processing`) are optional and
// populated only in debug mode. AssistantService's single projection chokepoint
// guarantees they're absent in non-debug, so there's no second wire type.
export const assistantConversationSchema = z
  .object({
    threadId: z.string(),
    agentId: z.string().optional(),
    actor: z.string().optional(),
    createdAt: z.string().optional(),
    model: z.string().optional(),
    provider: assistantProviderSchema.optional(),
    forcedCurrentDate: z.string().optional(),
    timezone: z.string().optional(),
    usage: chatUsageSchema.optional(),
    entries: z.array(threadEntrySchema),
    streaming: streamingSchema.optional(),
  })
  .strict()

export const assistantConversationOutputSchema = z
  .object({
    conversation: assistantConversationSchema,
    key: z.string().optional(),
  })
  .strict()
